import { MdOutlineEmail, MdKey } from "react-icons/md";
import PackageJson from "../../../package.json";
import { useAuth } from "../../hooks/auth";
import { SignInButton } from "./components/SignInButton";
import { Input } from "./components/Input";
import { CountrySelector } from "../../components/CountrySelector";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import * as S from "./styles";
import { Logo } from "./components/Logo";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

export function SignIn() {
  const { signIn, isLoading } = useAuth();
  const { t } = useTranslation();

  const { email: initialEmail } = useLocation().state || {};

  const createSchema = z.object({
    countryCode: z.string().min(1, { message: t("countryRequired") }),
    email: z.string().email(t("invalidEmail")),
    password: z
      .string({ required_error: t("enterYourPassword") })
      .min(1, { message: t("enterYourPassword") }),
  });

  type SignInSchema = z.infer<typeof createSchema>;

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SignInSchema>({
    resolver: zodResolver(createSchema),
  });

  const email = watch("email");

  useEffect(() => {
    if (initialEmail) {
      setValue("email", initialEmail);
    }
  }, [initialEmail]);

  async function handleSignIn(data: SignInSchema) {
    try {
      const { email, password, countryCode } = data;

      await signIn({
        email,
        password,
        countryCode,
      });
    } catch (err) {
      throw Error(String(err));
    }
  }

  return (
    <S.Container>
      <S.SignInContent onSubmit={handleSubmit(handleSignIn)}>
        <S.ImageContainer>
          <Logo />
        </S.ImageContainer>

        <S.InputAlign>
          <CountrySelector
            error={errors?.countryCode?.message}
            {...register("countryCode")}
            onCountryChange={(countryCode: string) =>
              setValue("countryCode", countryCode, { shouldValidate: true })
            }
          />

          <Input
            label={t("email")}
            icon={<MdOutlineEmail />}
            type="email"
            errorMessage={errors?.email?.message}
            {...register("email")}
          />

          <Input
            label={t("password")}
            isPassword
            icon={<MdKey />}
            errorMessage={errors?.password?.message}
            {...register("password")}
          />
        </S.InputAlign>

        <SignInButton
          type="submit"
          title={t("signIn")}
          isLoading={isLoading}
          width="100%"
          height="3.5rem"
        />

        <S.ForgotPasswordButton to={`/forgot-password?email=${email}`}>
          {t("forgotPassword")}
        </S.ForgotPasswordButton>

        <S.VersionContainer>
          <p>
            {t("version")} {PackageJson.version}
          </p>
        </S.VersionContainer>
      </S.SignInContent>
    </S.Container>
  );
}
